import React from 'react';
import { TouchableOpacity, Text, StyleSheet, ViewStyle, TextStyle, GestureResponderEvent, ActivityIndicator, View } from 'react-native';
import { RESIDENTS_DESKTOP_COLORS } from '@/constants/residents/desktop/color';

type ButtonVariant = 'primary' | 'secondary' | 'tertiary' | 'link' | 'outlined';
type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps {
  title: string;
  onPress?: (event: GestureResponderEvent) => void;
  disabled?: boolean;
  loading?: boolean;
  variant?: ButtonVariant;
  size?: ButtonSize;
  style?: ViewStyle;
  textStyle?: TextStyle;
  accessibilityLabel?: string;
  testID?: string;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
}

export const RNButton: React.FC<ButtonProps> = ({
  title,
  onPress,
  disabled = false,
  loading = false,
  variant = 'tertiary',
  size = 'md',
  style,
  textStyle,
  accessibilityLabel,
  testID,
  icon,
  iconPosition = 'left',
}) => {
  const containerStyles = [
    styles.base,
    size === 'sm' && styles.sm,
    size === 'md' && styles.md,
    size === 'lg' && styles.lg,
    variant === 'primary' && styles.primary,
    variant === 'secondary' && styles.secondary,
    variant === 'tertiary' && styles.tertiary,
    variant === 'link' && styles.link,
    variant === 'outlined' && styles.outlined,
    (disabled || loading) && styles.disabled,
    style,
  ];

  const labelStyles = [
    styles.label,
    variant === 'link' && styles.linkText,
    variant === 'outlined' && styles.outlinedText,
    textStyle,
  ];

  return (
    <TouchableOpacity
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel || title}
      onPress={onPress}
      disabled={disabled || loading}
      style={containerStyles}
      testID={testID}
    >
      {loading ? (
        <ActivityIndicator size="small" color={RESIDENTS_DESKTOP_COLORS.BUTTON_TERTIARY} />
      ) : (
        <View style={styles.content}>
          {icon && iconPosition === 'left' ? <View style={styles.icon}>{icon}</View> : null}
          <Text style={labelStyles}>{title}</Text>
          {icon && iconPosition === 'right' ? <View style={styles.icon}>{icon}</View> : null}
        </View>
      )}
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  base: {
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  sm: {
    paddingVertical: 8,
    paddingHorizontal: 12,
  },
  md: {
    paddingVertical: 10,
    paddingHorizontal: 16,
  },
  lg: {
    paddingVertical: 12,
    paddingHorizontal: 18,
  },
  label: {
    fontWeight: '700',
    color: RESIDENTS_DESKTOP_COLORS.SECONDARY_BACKGROUND,
  },
  content: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  icon: {
    marginRight: 6,
  },
  primary: {
    backgroundColor: RESIDENTS_DESKTOP_COLORS.BUTTON_PRIMARY,
  },
  secondary: {
    backgroundColor: RESIDENTS_DESKTOP_COLORS.BUTTON_SECONDARY,
  },
  tertiary: {
    backgroundColor: RESIDENTS_DESKTOP_COLORS.BUTTON_TERTIARY,
  },
  link: {
    backgroundColor: 'transparent',
    paddingVertical: 0,
    paddingHorizontal: 0,
  },
  linkText: {
    // default link color; callers can override via textStyle
    color: RESIDENTS_DESKTOP_COLORS.TEXT_SECONDARY,
  },
  outlined: {
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: RESIDENTS_DESKTOP_COLORS.BUTTON_TERTIARY,
  },
  outlinedText: {
    color: RESIDENTS_DESKTOP_COLORS.BUTTON_TERTIARY,
  },
  disabled: {
    opacity: 0.6,
  },
});

export default RNButton;


